import { PURCHASE_PLANS, getPlanFeatures, money, perMonth } from "@/consts/pricing";
import type { PurchasePlan } from "@/consts/pricing";
import type { FeatureRowId, FeatureValue } from "@/lib/compare/types";

export { money };

export const CRAYO_DEFINITION =
  "Crayo is an AI clipping editor at crayo.ai for clippers who turn long-form video into short clips for Instagram Reels, YouTube Shorts and TikTok. AutoClip finds and ranks the moments, reframes faces and captions them, and a timeline editor opens every clip, all in the browser.";

export const CRAYO_DISCLOSURE =
  "Crayo publishes this page and sells one of the products compared. Every competitor fact links to its source and carries the date we checked it. Spotted an error? Email support@crayo.ai and we correct it within 48 hours.";

export const CRAYO_PRICING_URL = "https://crayo.ai/pricing";
export const CRAYO_REFUND_URL = "https://crayo.ai/refund-policy";

export type CrayoPlanView = {
  name: string;
  monthly: number;
  quarterlyTotal: number;
  quarterlyPerMonth: number;
  yearlyTotal: number;
  yearlyPerMonth: number;
  credits: number;
  exportMinutes: number;
  voiceoverMinutes: number;
  images: number;
  storage: string;
  trackerCredits: number;
  features: string[];
};

const toView = (plan: PurchasePlan): CrayoPlanView => ({
  name: plan.name,
  monthly: plan.monthly,
  quarterlyTotal: plan.quarterly,
  quarterlyPerMonth: perMonth(plan, "quarterly"),
  yearlyTotal: plan.yearly,
  yearlyPerMonth: perMonth(plan, "yearly"),
  credits: plan.values.videos,
  exportMinutes: plan.values.exports / 60,
  voiceoverMinutes: plan.values.voiceovers / 60,
  images: plan.values.images,
  storage: plan.values.storage,
  trackerCredits: plan.values.trackerCredits,
  features: getPlanFeatures(plan),
});

export const CRAYO_PLANS: CrayoPlanView[] = PURCHASE_PLANS.map(toView);

export const crayoPlan = (name: string): CrayoPlanView =>
  CRAYO_PLANS.find((plan) => plan.name === name) ?? CRAYO_PLANS[0];

export const CRAYO_ENTRY = CRAYO_PLANS[0];

export const crayoEntryPriceLine = (): string =>
  `${money(CRAYO_ENTRY.monthly)}/mo, ${money(CRAYO_ENTRY.quarterlyPerMonth)}/mo billed quarterly, or ${money(
    CRAYO_ENTRY.yearlyTotal,
  )}/yr`;

export const crayoPlanTerms = (plan: CrayoPlanView): string =>
  `${money(plan.monthly)} a month, ${money(plan.quarterlyTotal)} every 3 months (${money(
    plan.quarterlyPerMonth,
  )} a month) or ${money(plan.yearlyTotal)} a year (about ${money(plan.yearlyPerMonth)} a month)`;

export const crayoEntryIncludes = (): string =>
  `${CRAYO_ENTRY.credits} clips (workflow credits), ${formatHours(CRAYO_ENTRY.exportMinutes)} of export, ${
    CRAYO_ENTRY.storage
  } storage, the timeline editor and the API; also ${CRAYO_ENTRY.voiceoverMinutes} voiceover minutes, ${
    CRAYO_ENTRY.images
  } AI images and ${CRAYO_ENTRY.trackerCredits.toLocaleString("en-US")} View Tracker credits a month`;

export const formatHours = (minutes: number): string => {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  return `${minutes} minutes`;
};

export const CRAYO_META = {
  meteringUnit: "1 workflow credit per finished clip, plus export minutes",
  freeTier: "None. Twelve free browser tools need no account and never upload the file.",
  trial: "None",
  exportResolution: "Up to 1080p",
  sources: "YouTube and TikTok links or file upload (up to 4 GB, 1 minute to 3 hours)",
  clipsPerRun: "1 to 20 clips per run, 15 to 120 seconds each",
  captionStyles: 32,
  captionLanguages: 56,
  apiEndpoints: 34,
  refunds: "No refunds. All sales are final.",
  cancel: "Cancel any time in one click from Settings. The plan stays active to the end of the cycle and nothing further is charged.",
  support: "Discord, 24/7, and support@crayo.ai",
};

export const CRAYO_FEATURES: Record<FeatureRowId, FeatureValue> = {
  longToShort: { v: "yes", note: "AutoClip, 1 to 20 clips per run" },
  momentRanking: { v: "yes", note: "Virality ranking in AutoClip and Find Viral Moments" },
  reframing: { v: "yes", note: "Face-aware camera track" },
  maxSource: { v: "text", note: "3 hours or 4 GB per upload" },
  layouts: { v: "text", note: "Speaker, streamer, split, full" },
  redditStory: { v: "yes" },
  fakeText: { v: "yes", note: "iMessage-style" },
  aiImages: { v: "yes", note: "100 to 500 per month by plan" },
  voiceover: { v: "yes", note: "Hundreds of voices, 30 to 180 minutes by plan" },
  voiceCloning: { v: "no" },
  avatars: { v: "no" },
  timelineEditor: { v: "yes", note: "Camera, keyframes, zooms, speed ramps" },
  captionStyles: { v: "text", note: `${CRAYO_META.captionStyles} styles` },
  captionLanguages: { v: "text", note: `${CRAYO_META.captionLanguages} spoken languages` },
  bRoll: { v: "partial", note: "Add your own clips in the editor" },
  fourK: { v: "no", note: "1080p maximum" },
  scheduling: { v: "no", note: "Download and post yourself" },
  analytics: { v: "partial", note: "View Tracker, not per-post stats" },
  socialTracker: { v: "yes", note: "Instagram, YouTube, TikTok; unlimited accounts on monthly credits" },
  api: { v: "yes", note: `${CRAYO_META.apiEndpoints} endpoints on every plan` },
  webhooks: { v: "yes" },
  web: { v: "yes" },
  desktop: { v: "no" },
  mobile: { v: "no", note: "Browser app only" },
  teamSeats: { v: "no" },
  freeTier: { v: "no", note: "Free browser tools only" },
  trial: { v: "no" },
  metering: { v: "text", note: "1 credit per finished clip" },
  rollover: { v: "no", note: "Top-up packs are separate" },
  refunds: { v: "text", note: "No refunds, all sales final" },
  cancelInApp: { v: "yes", note: "One click in Settings" },
};
